import { WIDTH_CONSTANTS } from '@theme/widthConstants';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseDragResizeOptions {
  minWidth: number;
  maxWidth: number;
  defaultWidth: number;
  onWidthChange: (width: number) => void;
  currentWidth: number;
  isEnabled?: boolean;
  onDragEnd: (width: number) => void;
}

interface UseDragResizeReturn {
  isDragging: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
  dragHandleProps: {
    onMouseDown: (e: React.MouseEvent) => void;
    sx: any;
  };
}

export const useDragResize = ({
  minWidth,
  maxWidth,
  defaultWidth: _defaultWidth,
  onWidthChange,
  currentWidth,
  onDragEnd,
  isEnabled = true,
}: UseDragResizeOptions): UseDragResizeReturn => {
  const [isDragging, setIsDragging] = useState(false);
  const animationFrameRef = useRef<number | null>(null);

  const smoothSetWidth = useCallback(
    (newWidth: number) => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(() => {
        onWidthChange(newWidth);
      });
    },
    [onWidthChange],
  );

  useEffect(() => {
    if (!isEnabled) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      const windowMaxWidth = maxWidth ?? Math.floor(window.innerWidth * 0.45);
      const currentMaxWidth = Math.min(maxWidth, windowMaxWidth);
      const newWidth = Math.min(Math.max(e.clientX - WIDTH_CONSTANTS.SIDEBAR_WIDTH, minWidth), currentMaxWidth);
      smoothSetWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      onDragEnd(currentWidth);
      document.body.style.cursor = '';
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isDragging, smoothSetWidth, isEnabled, minWidth, currentWidth, onDragEnd, maxWidth]);

  // Add window resize listener to ensure sidebar never exceeds 45% of window width
  useEffect(() => {
    if (!isEnabled) return;

    const handleWindowResize = () => {
      const currentMaxWidth = Math.floor(window.innerWidth * 0.45);

      if (currentWidth > currentMaxWidth) {
        smoothSetWidth(currentMaxWidth);
      }
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [isEnabled, smoothSetWidth, currentWidth]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isEnabled) return;

      e.preventDefault();
      setIsDragging(true);
    },
    [isEnabled],
  );

  const dragHandleProps = {
    onMouseDown: handleMouseDown,
    sx: (theme: any) => ({
      width: 8,
      cursor: 'col-resize',
      background: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.border.default,
      zIndex: 1400,
      position: 'absolute',
      right: '-3px',
      top: 0,
      height: '100%',
      transition: 'background 0.2s',
      '&:hover': {
        background: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#bdbdbd',
      },
    }),
  };

  return {
    isDragging,
    handleMouseDown,
    dragHandleProps,
  };
};
